(function () {
  const storageKey = 'ubackdrop.activeCurrency';
  const targetSelectors = [
    '#CartDrawer .money',
    '#main-cart-items .money',
    '#main-cart-footer .money',
    'cart-notification .money',
    '[data-discount-code-money]',
  ];
  const cartMutationSelector =
    '#CartDrawer, cart-drawer, cart-drawer-items, #main-cart-items, #main-cart-footer, cart-notification, .cart-drawer__footer, [data-active-discount-code], [data-discount-code-money]';
  const retryDelays = [0, 120, 350, 900, 1800];
  const currencyCountryMap = {
    USD: 'US',
    CAD: 'CA',
    AUD: 'AU',
    GBP: 'GB',
    EUR: 'ES',
  };
  let mutationObserver = null;
  let pendingRefreshTimer = null;
  let refreshTimers = [];
  let ignoreMutationUntil = 0;
  let checkoutSyncInProgress = false;

  function normalizeCurrencyCode(value) {
    const code = (value || '').trim().toUpperCase();
    return /^[A-Z]{3}$/.test(code) ? code : '';
  }

  function getDoublyCurrencyFromText(text) {
    const cleanText = (text || '').trim();
    const currencyLabels = {
      'US DOLLAR': 'USD',
      'AUSTRALIAN DOLLAR': 'AUD',
      'CANADIAN DOLLAR': 'CAD',
      EURO: 'EUR',
      'BRITISH POUND STERLING': 'GBP',
    };
    const labelCurrency = currencyLabels[cleanText.toUpperCase()];
    if (labelCurrency) return labelCurrency;

    const match = cleanText.match(/\b[A-Z]{3}\b/);
    return normalizeCurrencyCode(match?.[0]);
  }

  function isElementVisible(element) {
    return !!(element?.offsetWidth || element?.offsetHeight || element?.getClientRects?.().length);
  }

  function getDoublyApi() {
    return window.DoublyGlobalCurrency || window.DoublyCurrency || null;
  }

  function readDoublyCookie() {
    try {
      return normalizeCurrencyCode(getDoublyApi()?.cookie?.read?.());
    } catch (error) {
      return '';
    }
  }

  function readStoredCurrency() {
    try {
      return normalizeCurrencyCode(window.localStorage.getItem(storageKey));
    } catch (error) {
      return '';
    }
  }

  function setStoredCurrency(currencyCode) {
    const activeCurrency = normalizeCurrencyCode(currencyCode);
    if (!activeCurrency) return '';

    try {
      window.localStorage.setItem(storageKey, activeCurrency);
    } catch (error) {
      // Storage can be unavailable in restricted preview contexts.
    }

    try {
      getDoublyApi()?.cookie?.write?.(activeCurrency);
    } catch (error) {
      // Beast/Doubly is an external app script; UI sync must not depend on cookie writes.
    }

    return activeCurrency;
  }

  function getSelectedControlCurrency() {
    const visibleCurrent = Array.from(document.querySelectorAll('.doubly-nice-select .current, .doubly-float .current'))
      .filter(isElementVisible)
      .map((element) => getDoublyCurrencyFromText(element.textContent))
      .find(Boolean);
    if (visibleCurrent) return visibleCurrent;

    const selectedOption = Array.from(document.querySelectorAll('.doubly-nice-select .option.selected, .doubly-nice-select .option.focus'))
      .map((option) => normalizeCurrencyCode(option.getAttribute('data-value')) || getDoublyCurrencyFromText(option.textContent))
      .find(Boolean);
    if (selectedOption) return selectedOption;

    return Array.from(document.querySelectorAll('[name="doubly-currencies"]'))
      .map((select) => normalizeCurrencyCode(select.value))
      .find(Boolean) || '';
  }

  function getActiveCurrency() {
    const controlCurrency = getSelectedControlCurrency();
    if (controlCurrency) return controlCurrency;

    const apiCurrency = normalizeCurrencyCode(getDoublyApi()?.currentCurrency);
    if (apiCurrency) return apiCurrency;

    const cookieCurrency = readDoublyCookie();
    if (cookieCurrency) return cookieCurrency;

    return readStoredCurrency();
  }

  function restoreControls(currencyCode) {
    const activeCurrency = normalizeCurrencyCode(currencyCode);
    if (!activeCurrency) return;

    document.querySelectorAll('[name="doubly-currencies"]').forEach((select) => {
      const hasOption = Array.from(select.options || []).some((option) => option.value === activeCurrency);
      if (hasOption) select.value = activeCurrency;
    });

    document.querySelectorAll('.doubly-nice-select').forEach((select) => {
      const options = Array.from(select.querySelectorAll('.option'));
      const activeOption = options.find(
        (option) =>
          normalizeCurrencyCode(option.getAttribute('data-value')) === activeCurrency ||
          getDoublyCurrencyFromText(option.textContent) === activeCurrency
      );
      if (!activeOption) return;

      options.forEach((option) => option.classList.remove('selected', 'focus'));
      activeOption.classList.add('selected', 'focus');

      const current = select.querySelector('.current');
      if (current) current.innerHTML = activeOption.innerHTML;
    });

    document.querySelectorAll('.currency-switcher-btn[doubly-currency]').forEach((button) => {
      button.classList.toggle('selected', button.getAttribute('doubly-currency') === activeCurrency);
    });
  }

  function convertSelector(currencyCode, selector) {
    const api = getDoublyApi();
    if (!api?.convertAll) return false;

    try {
      api.currentCurrency = currencyCode;
      if (selector) {
        api.convertAll(currencyCode, selector);
      } else {
        api.convertAll(currencyCode);
      }
      return true;
    } catch (error) {
      return false;
    }
  }

  function applyCurrency(currencyCode, options = {}) {
    const activeCurrency = normalizeCurrencyCode(currencyCode) || getActiveCurrency();
    if (!activeCurrency) return '';

    setStoredCurrency(activeCurrency);
    restoreControls(activeCurrency);

    if (!options.skipConvert) {
      if (options.selector) {
        convertSelector(activeCurrency, options.selector);
      } else if (Array.isArray(options.selectors)) {
        options.selectors.forEach((selector) => convertSelector(activeCurrency, selector));
      } else {
        convertSelector(activeCurrency);
      }
    }

    restoreControls(activeCurrency);
    return activeCurrency;
  }

  function refreshTargets(currencyCode) {
    const activeCurrency = normalizeCurrencyCode(currencyCode) || getActiveCurrency();
    if (!activeCurrency) return;

    setStoredCurrency(activeCurrency);
    restoreControls(activeCurrency);
    ignoreMutationUntil = Date.now() + 250;
    targetSelectors.forEach((selector) => convertSelector(activeCurrency, selector));
    restoreControls(activeCurrency);
  }

  function clearScheduledRefreshes() {
    clearTimeout(pendingRefreshTimer);
    refreshTimers.forEach((timer) => clearTimeout(timer));
    refreshTimers = [];
  }

  function scheduleRefresh(currencyCode) {
    const activeCurrency = normalizeCurrencyCode(currencyCode) || getActiveCurrency();
    if (!activeCurrency) return;

    clearScheduledRefreshes();
    retryDelays.forEach((delay) => {
      refreshTimers.push(window.setTimeout(() => refreshTargets(activeCurrency), delay));
    });
    pendingRefreshTimer = window.setTimeout(() => restoreControls(activeCurrency), retryDelays[retryDelays.length - 1] + 120);
  }

  function bindCurrencyControls() {
    document.addEventListener(
      'click',
      (event) => {
        const option = event.target.closest('.doubly-nice-select .option[data-value], .currency-switcher-btn[doubly-currency]');
        if (!option) return;

        const currency = normalizeCurrencyCode(option.getAttribute('data-value') || option.getAttribute('doubly-currency'));
        if (currency) {
          setStoredCurrency(currency);
          window.setTimeout(() => scheduleRefresh(currency), 0);
        }
      },
      true
    );

    document.addEventListener(
      'change',
      (event) => {
        if (!event.target.matches('[name="doubly-currencies"]')) return;
        const currency = normalizeCurrencyCode(event.target.value);
        if (currency) {
          setStoredCurrency(currency);
          scheduleRefresh(currency);
        }
      },
      true
    );
  }

  function bindCartEvents() {
    if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof subscribe === 'function') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        scheduleRefresh();
      });
    }

    document.addEventListener('cart:updated', () => scheduleRefresh());
    document.addEventListener('shopify:section:load', () => scheduleRefresh());
  }

  function getCountryForCurrency(currencyCode) {
    return currencyCountryMap[normalizeCurrencyCode(currencyCode)] || '';
  }

  function getLocalizationCountryForm() {
    return (
      document.querySelector('form.localization-form input[name="country_code"]')?.closest('form') ||
      document.querySelector('form[action*="/localization"] input[name="country_code"]')?.closest('form') ||
      document.querySelector('form[action*="/localization"] select[name="country_code"]')?.closest('form')
    );
  }

  function getCurrentLocalizationCountry() {
    return normalizeCountryCode(document.querySelector('form.localization-form [name="country_code"]')?.value);
  }

  function normalizeCountryCode(value) {
    const code = (value || '').trim().toUpperCase();
    return /^[A-Z]{2}$/.test(code) ? code : '';
  }

  function hasLocalizationCountry(countryCode) {
    const country = normalizeCountryCode(countryCode);
    if (!country) return false;

    return Boolean(
      document.querySelector(`form[action*="/localization"] [name="country_code"] option[value="${country}"]`) ||
        document.querySelector(`form[action*="/localization"] a[data-value="${country}"]`) ||
        document.querySelector('form[action*="/localization"] input[name="country_code"]')
    );
  }

  async function syncLocalizationCountry(countryCode) {
    const country = normalizeCountryCode(countryCode);
    const form = getLocalizationCountryForm();
    if (!country || !form || !hasLocalizationCountry(country)) return false;

    const currentCountry = getCurrentLocalizationCountry();
    if (currentCountry === country) return true;

    const countryInput = form.querySelector('[name="country_code"]');
    if (countryInput) countryInput.value = country;

    const formData = new FormData(form);
    formData.set('country_code', country);
    if (!formData.has('return_to')) {
      formData.set('return_to', window.location.pathname + window.location.search);
    }

    try {
      const response = await fetch(form.action || '/localization', {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
      });
      return response.ok || response.redirected;
    } catch (error) {
      return false;
    }
  }

  function submitCheckoutForm(form, submitter) {
    if (form?.requestSubmit && submitter) {
      form.requestSubmit(submitter);
      return;
    }

    if (form?.requestSubmit) {
      form.requestSubmit();
      return;
    }

    form?.submit?.();
  }

  function bindCheckoutCurrencySync() {
    document.addEventListener(
      'click',
      async (event) => {
        const checkoutButton = event.target.closest('button[name="checkout"], input[name="checkout"]');
        if (!checkoutButton || checkoutSyncInProgress) return;

        const form = checkoutButton.form || checkoutButton.closest('form');
        const activeCurrency = getActiveCurrency();
        const targetCountry = getCountryForCurrency(activeCurrency);
        if (!form || !targetCountry || getCurrentLocalizationCountry() === targetCountry) return;

        event.preventDefault();
        checkoutSyncInProgress = true;
        checkoutButton.setAttribute('aria-disabled', 'true');

        await syncLocalizationCountry(targetCountry);

        checkoutSyncInProgress = false;
        checkoutButton.removeAttribute('aria-disabled');
        submitCheckoutForm(form, checkoutButton);
      },
      true
    );

    document.addEventListener(
      'submit',
      async (event) => {
        const form = event.target;
        if (checkoutSyncInProgress || !form?.matches?.('form#cart, form#CartDrawer-Form')) return;

        const submitter = event.submitter;
        if (submitter && !submitter.matches?.('button[name="checkout"], input[name="checkout"]')) return;

        const activeCurrency = getActiveCurrency();
        const targetCountry = getCountryForCurrency(activeCurrency);
        if (!targetCountry || getCurrentLocalizationCountry() === targetCountry) return;

        event.preventDefault();
        checkoutSyncInProgress = true;
        await syncLocalizationCountry(targetCountry);
        checkoutSyncInProgress = false;
        submitCheckoutForm(form, submitter);
      },
      true
    );
  }

  function observeCartMutations() {
    if (mutationObserver || !document.body) return;

    mutationObserver = new MutationObserver((mutations) => {
      if (Date.now() < ignoreMutationUntil) return;

      const hasCartChange = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node.nodeType === 1 &&
            (node.matches?.(cartMutationSelector) || node.querySelector?.(cartMutationSelector))
        )
      );

      if (hasCartChange) {
        scheduleRefresh();
      }
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  function init() {
    bindCurrencyControls();
    bindCartEvents();
    bindCheckoutCurrencySync();
    observeCartMutations();
    scheduleRefresh(readDoublyCookie() || readStoredCurrency());
  }

  window.UBackdropCurrencySync = {
    applyCurrency,
    getActiveCurrency,
    refreshTargets: scheduleRefresh,
    restoreControls,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
